import "./Version";
import "./ConnectJobHistory";
import "./TadSheet";
import {
  FirestoreCollectionProsessorOptions,
  createFirestoreCollectionProsessor,
} from "../firebase/firestore";

export const organizationKindList = [
  "optium:project",
  "optium:demo",
  "optium:ship",
  "optium:plant",
  "org:enterprise",
  "org:pro",
  "org:free",
  "planium:demo",
  "planium:chemical",
  "dldx:project",
] as const;

export const organizationServiceTypes = ["optium", "planium"] as const;

// 探索型DX組織かどうか。LDS WS の接続先を lds-exp に振り分けるために使う。
export const isExplorativeDxOrgKind = (
  orgKind: (typeof organizationKindList)[number] | null | undefined
) => !!orgKind && orgKind.startsWith("dldx");

export const organizationOptimizationTypes = ["new", "overwrite"] as const;

export type OrganizationRaw = {
  org_id: string;
  name: string;
  short_name: string;
  org_kind: (typeof organizationKindList)[number];
  num_org_members: number;
  max_num_org_members: number | null; // nullは無制限
  description: string;
  default_algorithm_version_id: string;
  is_mfa_required: null | boolean;
  is_disabled: boolean;
  created_at: string;
  updated_at: string;
  is_file_optimize_enabled: null | boolean;
  is_sheet_enabled?: null | boolean;
  is_tads_enabled?: boolean;
  // 標準ヘッダー・サイドメニューを表示するか (false = ヘッダーのみ表示モード)。
  is_sidebar_enabled: boolean;
  optimization_type: (typeof organizationOptimizationTypes)[number] | null;
  service_status: "under_maintenance" | null;
  maintenance_description: string | null;
  // 組織遷移時にデフォルト表示する extraPage の view_type（extra_page_id）。空文字の場合はバージョン一覧を表示する
  initial_extra_page_id: string;
};

export type OrganizationsItemRaw = Pick<
  OrganizationRaw,
  | "org_id"
  | "name"
  | "short_name"
  | "org_kind"
  | "num_org_members"
  | "description"
  | "is_mfa_required"
  | "is_sheet_enabled"
  | "is_file_optimize_enabled"
  | "service_status"
  | "initial_extra_page_id"
> & { is_owner: boolean };

export type OrganizationMfaRawForm = Pick<OrganizationRaw, "is_mfa_required">;

export const initOrganizationRaw: OrganizationRaw = {
  org_id: "",
  name: "",
  short_name: "",
  org_kind: organizationKindList[0],
  num_org_members: 0,
  max_num_org_members: 0,
  description: "",
  default_algorithm_version_id: "",
  is_mfa_required: false,
  is_disabled: false,
  created_at: "",
  updated_at: "",
  is_file_optimize_enabled: true,
  is_sheet_enabled: false,
  is_tads_enabled: false,
  is_sidebar_enabled: true,
  optimization_type: "new",
  service_status: null,
  maintenance_description: null,
  initial_extra_page_id: "",
};

export type Organization = {
  id: string;
  name: string;
  shortName: string;
  orgKind: (typeof organizationKindList)[number];
  numOrgMembers: number;
  maxNumOrgMembers: number | null;
  description: string;
  defaultAlgorithmVersionId: string;
  isMfaRequired: null | boolean;
  isDisabled: boolean;
  createdAt: string;
  updatedAt: string;
  isFileOptimizeEnabled: null | boolean;
  isSheetEnabled?: null | boolean;
  isTadsEnabled: boolean;
  isSidebarEnabled: boolean;
  optimizationType: (typeof organizationOptimizationTypes)[number] | null;
  serviceStatus: "under_maintenance" | null;
  maintenanceDescription: string | null;
  initialExtraPageId: string;
};

const processorOptions: FirestoreCollectionProsessorOptions<
  Organization,
  OrganizationRaw,
  Record<string, string>
> = {
  getCollectionPath: ({ organizationId, versionId, sheetId }) =>
    `/app/spreadsheet_ui/v/1/organizations`,
  toFirestore: ({
    id,
    name,
    shortName,
    orgKind,
    numOrgMembers,
    maxNumOrgMembers,
    description,
    defaultAlgorithmVersionId,
    isMfaRequired,
    isDisabled,
    createdAt,
    updatedAt,
    isFileOptimizeEnabled,
    isSheetEnabled,
    isTadsEnabled,
    isSidebarEnabled,
    optimizationType,
    serviceStatus,
    maintenanceDescription,
    initialExtraPageId,
  }) => ({
    org_id: id,
    name,
    short_name: shortName,
    org_kind: orgKind,
    num_org_members: numOrgMembers,
    max_num_org_members: maxNumOrgMembers,
    description,
    default_algorithm_version_id: defaultAlgorithmVersionId,
    is_mfa_required: isMfaRequired,
    is_disabled: isDisabled,
    created_at: createdAt,
    updated_at: updatedAt,
    is_file_optimize_enabled: isFileOptimizeEnabled,
    is_sheet_enabled: isSheetEnabled,
    is_tads_enabled: isTadsEnabled,
    is_sidebar_enabled: isSidebarEnabled,
    optimization_type: optimizationType,
    service_status: serviceStatus,
    maintenance_description: maintenanceDescription,
    initial_extra_page_id: initialExtraPageId,
  }),
  fromFirestore: ({
    org_id,
    name,
    short_name,
    org_kind,
    num_org_members,
    max_num_org_members,
    description,
    default_algorithm_version_id,
    is_mfa_required,
    is_disabled,
    created_at,
    updated_at,
    is_file_optimize_enabled,
    is_sheet_enabled,
    is_tads_enabled,
    is_sidebar_enabled,
    optimization_type,
    service_status,
    maintenance_description,
    initial_extra_page_id,
  }) => ({
    id: org_id,
    name,
    shortName: short_name,
    orgKind: org_kind,
    numOrgMembers: num_org_members,
    maxNumOrgMembers: max_num_org_members,
    description,
    defaultAlgorithmVersionId: default_algorithm_version_id,
    isMfaRequired: is_mfa_required,
    isDisabled: is_disabled,
    createdAt: created_at,
    updatedAt: updated_at,
    isFileOptimizeEnabled: is_file_optimize_enabled,
    isSheetEnabled: is_sheet_enabled,
    // 利用開始前の組織にはフィールドが無いので、欠損は「未利用」に寄せる。
    isTadsEnabled: is_tads_enabled ?? false,
    isSidebarEnabled: is_sidebar_enabled,
    optimizationType: optimization_type,
    serviceStatus: service_status,
    maintenanceDescription: maintenance_description ?? null,
    // 移行期に未設定(undefined)の値が来ても安全側に空文字へ寄せる。
    initialExtraPageId: initial_extra_page_id ?? "",
  }),
};
export const organizationToFirestore = processorOptions.toFirestore;

export const { useItem: useOrganization } =
  createFirestoreCollectionProsessor(processorOptions);

export type GenericNotificationChannels = {
  email: { email: string } | null;
  slack_webhook: { webhook_url: string } | null;
};
